import React, { useState, useCallback, ReactNode, useMemo } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
  DragOverEvent,
  defaultDropAnimationSideEffects,
  DropAnimation,
  MeasuringStrategy,
} from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { createPortal } from 'react-dom';
import { useFieldMapping } from '../../contexts/FieldMappingContext';
import { CrmField } from '../DraggableCrmField';
import { DraggableFieldOverlay } from 'src/pages/Automations/components/FieldMapping/DraggableFieldOverlay';
import { FieldMappingDndContextProvider } from 'src/pages/Automations/components/FieldMapping/FieldMappingDndContext';

interface FieldMappingDndProviderProps {
  children: ReactNode;
}

export const FieldMappingDndProvider: React.FC<
  FieldMappingDndProviderProps
> = ({ children }) => {
  const {
    availableCrmFields,
    filteredCrmFields,
    mappingEntries,
    addFieldToMapping,
    removeFieldFromMapping,
    reorderFieldsInMapping,
  } = useFieldMapping();

  const [activeField, setActiveField] = useState<CrmField | null>(null);
  const [activeSourceContainer, setActiveSourceContainer] = useState<
    string | null
  >(null);
  const [overContainerId, setOverContainerId] = useState<string | null>(null);
  const [insertIndex, setInsertIndex] = useState<number>(-1);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 3,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const measuring = {
    droppable: {
      strategy: MeasuringStrategy.Always,
    },
  };

  // Find which container a field belongs to
  const findContainer = useCallback(
    (fieldValue: string): string | null => {
      // First check if the field is mapped to any target
      for (const mapping of mappingEntries) {
        if (mapping.crmFieldValues.includes(fieldValue)) {
          return mapping.campaignField;
        }
      }

      // If not mapped, check if it's in the source (unmapped fields)
      if (filteredCrmFields.some((f) => f.value === fieldValue)) {
        return 'source';
      }

      return null;
    },
    [filteredCrmFields, mappingEntries],
  );

  // Find a CrmField by its value
  const findCrmField = useCallback(
    (fieldValue: string): CrmField | null => {
      // Check available fields
      const availableField = availableCrmFields.find(
        (f) => f.value === fieldValue,
      );
      if (availableField) return availableField;

      return null;
    },
    [availableCrmFields],
  );

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const { active } = event;
      const fieldValue = active.id as string;
      const field = findCrmField(fieldValue);
      const container = findContainer(fieldValue);
      setActiveField(field);
      setActiveSourceContainer(container);
    },
    [findCrmField, findContainer],
  );

  const handleDragOver = useCallback(
    (event: DragOverEvent) => {
      const { active, over } = event;

      if (!over) {
        setOverContainerId(null);
        setInsertIndex(-1);
        return;
      }

      const overId = over.id as string;

      // Check if overId is a campaign field (target container)
      const isTargetContainer = mappingEntries.some(
        (m) => m.campaignField === overId,
      );

      // Check if overId is the source container or a field in source
      const isSourceContainer = overId === 'source';
      const isFieldInSource = filteredCrmFields.some((f) => f.value === overId);

      let targetContainer: string | null = null;
      let newIndex = -1;

      if (isSourceContainer) {
        // Hovering over empty source container - add at end
        targetContainer = 'source';
        newIndex = filteredCrmFields.length;
      } else if (isFieldInSource) {
        // Hovering over a field in source - calculate insert index
        targetContainer = 'source';
        const overIndex = filteredCrmFields.findIndex(
          (f) => f.value === overId,
        );
        if (overIndex !== -1 && active.rect.current.translated) {
          const draggedCenter =
            active.rect.current.translated.top +
            active.rect.current.translated.height / 2;
          const overCenter = over.rect.top + over.rect.height / 2;
          const isBelowOverItem = draggedCenter > overCenter;
          newIndex = isBelowOverItem ? overIndex + 1 : overIndex;
        } else {
          newIndex = filteredCrmFields.length;
        }
      } else if (isTargetContainer) {
        targetContainer = overId;
        // Dropping on empty container or container itself - add at end
        const mapping = mappingEntries.find((m) => m.campaignField === overId);
        newIndex = mapping?.crmFieldValues.length ?? 0;
      } else {
        // overId is a field, find its container
        const container = findContainer(overId);
        if (container && container !== 'source') {
          targetContainer = container;
          const mapping = mappingEntries.find(
            (m) => m.campaignField === container,
          );
          if (mapping) {
            const overIndex = mapping.crmFieldValues.findIndex(
              (value) => value === overId,
            );
            if (overIndex !== -1 && active.rect.current.translated) {
              // Use center-to-center comparison for more responsive swapping
              const draggedCenter =
                active.rect.current.translated.top +
                active.rect.current.translated.height / 2;
              const overCenter = over.rect.top + over.rect.height / 2;
              const isBelowOverItem = draggedCenter > overCenter;

              newIndex = isBelowOverItem ? overIndex + 1 : overIndex;
            }
          }
        }
      }

      setOverContainerId(targetContainer);
      setInsertIndex(newIndex);
    },
    [mappingEntries, filteredCrmFields, findContainer],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      const droppedOverContainerId = overContainerId;
      const droppedInsertIndex = insertIndex;

      setActiveField(null);
      setActiveSourceContainer(null);
      setOverContainerId(null);
      setInsertIndex(-1);

      if (!over) return;

      const activeId = active.id as string;
      const overId = over.id as string;

      const activeContainer = findContainer(activeId);
      const activeField = findCrmField(activeId);

      if (!activeField) return;

      // Determine if we're dropping on a target container
      // The overId could be a container id (campaignField) or another field id
      let targetContainer: string | null = null;

      // Check if overId is the source container or a field in source
      const isSourceContainer =
        overId === 'source' ||
        filteredCrmFields.some((f) => f.value === overId);
      if (isSourceContainer) {
        targetContainer = 'source';
      } else {
        // Prefer the container computed during drag-over (matches placeholder logic)
        if (droppedOverContainerId && droppedOverContainerId !== 'source') {
          targetContainer = droppedOverContainerId;
        }

        // Check if overId is a campaign field (target container)
        if (!targetContainer) {
          const isTargetContainer = mappingEntries.some(
            (m) => m.campaignField === overId,
          );
          if (isTargetContainer) {
            targetContainer = overId;
          } else {
            // overId is a field, find its container
            targetContainer = findContainer(overId);
          }
        }
      }

      // If dropping back to source from a target, remove from mapping
      if (targetContainer === 'source') {
        if (activeContainer && activeContainer !== 'source') {
          removeFieldFromMapping(activeContainer, activeId);
        }
        return;
      }

      if (!targetContainer) return;

      const targetMapping = mappingEntries.find(
        (m) => m.campaignField === targetContainer,
      );
      const targetLength = targetMapping?.crmFieldValues.length ?? 0;
      const dropIndex =
        droppedOverContainerId === targetContainer && droppedInsertIndex >= 0
          ? Math.max(0, Math.min(droppedInsertIndex, targetLength))
          : targetLength;

      // If dragging from source to a target
      if (activeContainer === 'source') {
        addFieldToMapping(targetContainer, activeId, { index: dropIndex });
        return;
      }

      // If reordering within the same target container
      if (activeContainer === targetContainer) {
        if (!targetMapping) return;

        const fromIndex = targetMapping.crmFieldValues.findIndex(
          (value) => value === activeId,
        );

        // If dropping on the container background, move to the end
        if (overId === targetContainer) {
          const toIndex = targetMapping.crmFieldValues.length - 1;
          if (fromIndex !== toIndex) {
            reorderFieldsInMapping(targetContainer, fromIndex, toIndex);
          }
          return;
        }

        const toIndex = targetMapping.crmFieldValues.findIndex(
          (value) => value === overId,
        );

        if (fromIndex === -1 || toIndex === -1) return;

        if (fromIndex !== toIndex) {
          reorderFieldsInMapping(targetContainer, fromIndex, toIndex);
        }
        return;
      }

      // Moving from one target to another
      if (activeContainer && activeContainer !== targetContainer) {
        removeFieldFromMapping(activeContainer, activeId);
        addFieldToMapping(targetContainer, activeId, { index: dropIndex });
        return;
      }
    },
    [
      findContainer,
      findCrmField,
      filteredCrmFields,
      mappingEntries,
      addFieldToMapping,
      removeFieldFromMapping,
      reorderFieldsInMapping,
      overContainerId,
      insertIndex,
    ],
  );

  const dropAnimation: DropAnimation = {
    duration: 200,
    easing: 'ease',
    sideEffects: defaultDropAnimationSideEffects({
      styles: {
        active: {
          opacity: '0.5',
        },
      },
    }),
  };

  const dragState = useMemo(
    () => ({
      activeFieldId: activeField?.value ?? null,
      activeSourceContainer,
      overContainerId,
      insertIndex,
    }),
    [activeField, activeSourceContainer, overContainerId, insertIndex],
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      measuring={measuring}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <FieldMappingDndContextProvider value={dragState}>
        {children}
      </FieldMappingDndContextProvider>

      {createPortal(
        <DragOverlay dropAnimation={dropAnimation}>
          {activeField ? <DraggableFieldOverlay field={activeField} /> : null}
        </DragOverlay>,
        document.body,
      )}
    </DndContext>
  );
};
